/**
 * The section vocabulary a page can be assembled from.
 *
 * This is the domain model: what a section *is*, independent of where it came
 * from. Nothing here knows about JSON, about the CMS, or about React. The
 * translation from the editing system's shape into these types lives in
 * adapters/secondary/content/blockMapper.ts, and the rendering of them in
 * adapters/primary/blocks/.
 *
 * Every block carries its own presentation (tone, width, alignment) rather than
 * having it split off into a separate layout structure. That is deliberate: a
 * section and how it is set are one thing to a reader and one thing to an
 * editor, and separating them would only mean joining them back together at
 * every use.
 */

export type BlockTone = 'default' | 'offWhite' | 'light' | 'brand';
export type BlockWidth = 'sm' | 'md' | 'lg' | 'xl';
export type BlockAlign = 'left' | 'center';

export const BLOCK_TONES: BlockTone[] = ['default', 'offWhite', 'light', 'brand'];
/**
 * `xl` is the width the hand-written sections use, so it is what an editor
 * needs to make a block sit flush with the rest of the page; the narrower three
 * remain for a block that should deliberately stand in from it.
 */
export const BLOCK_WIDTHS: BlockWidth[] = ['sm', 'md', 'lg', 'xl'];
export const BLOCK_ALIGNS: BlockAlign[] = ['left', 'center'];

/** A button or link. `url` may be an internal path or an external address. */
export interface BlockLink {
  label: string;
  url: string;
}

/**
 * Where a section an editor added to a built-in page appears: the name of the
 * page section it is placed before, or END for below everything.
 *
 * Only meaningful for the built-in pages. Pages assembled in the editing system
 * are one ordered list already, so their sections carry the default.
 */
export const BLOCK_PLACEMENT_END = 'end';

interface BaseBlock {
  /** Optional anchor, so an editor can link to a section directly. */
  anchor?: string;
  tone: BlockTone;
  placement: string;
}

export interface TextBlock extends BaseBlock {
  type: 'text';
  eyebrow?: string;
  title?: string;
  paragraphs: string[];
  width: BlockWidth;
  align: BlockAlign;
}

export interface CardsBlock extends BaseBlock {
  type: 'cards';
  title?: string;
  description?: string;
  items: Array<{ title: string; description: string }>;
  align: BlockAlign;
}

export interface ListBlock extends BaseBlock {
  type: 'list';
  title?: string;
  description?: string;
  items: string[];
  width: BlockWidth;
}

export interface TeasersBlock extends BaseBlock {
  type: 'teasers';
  title?: string;
  items: Array<{ title: string; description: string; link?: BlockLink }>;
}

export interface CtaBlock extends BaseBlock {
  type: 'cta';
  /**
   * Optional, so that a section can be one button and nothing else - which is
   * what a page whose heading is already carried above it needs. A block with
   * neither a heading nor a button is what gets dropped, not one without a
   * heading: the editing interface offers the heading as optional, and an
   * editor who clears it should get a button, not a section that vanishes.
   */
  title?: string;
  description?: string;
  link?: BlockLink;
}

export interface QuoteBlock extends BaseBlock {
  type: 'quote';
  text: string;
  source?: string;
  width: BlockWidth;
}

export interface ImageBlock extends BaseBlock {
  type: 'image';
  src: string;
  alt: string;
  caption?: string;
  width: BlockWidth;
}

/**
 * One document offered for download.
 *
 * `file` is an address, normally a path under /uploads to a file uploaded in
 * the editing system. `label` is what the link says, and is never empty - see
 * the fallback in blockMapper.ts.
 */
export interface DownloadItem {
  file: string;
  label: string;
  description?: string;
}

export interface DownloadsBlock extends BaseBlock {
  type: 'downloads';
  title?: string;
  description?: string;
  items: DownloadItem[];
  width: BlockWidth;
}

/**
 * One entry in a link list. `url` may be an internal path ("/organisation"), an
 * anchor on the same page ("#kontakt") or a full external address.
 */
export interface LinkItem {
  label: string;
  url: string;
  description?: string;
}

export interface LinksBlock extends BaseBlock {
  type: 'links';
  title?: string;
  description?: string;
  items: LinkItem[];
  width: BlockWidth;
}

export interface FaqBlock extends BaseBlock {
  type: 'faq';
  title?: string;
  description?: string;
  items: Array<{ question: string; answer: string }>;
  width: BlockWidth;
}

/**
 * One tile: a question or a theme, the short answer folded away behind it, and
 * optionally a link to where the full material lives.
 *
 * `text` is optional because a tile can be a signpost and nothing else - a
 * title and a link to a leaflet elsewhere. Without it the tile does not fold
 * open at all, which is better than a control that reveals nothing.
 */
export interface TopicItem {
  title: string;
  /**
   * The symbol above the title, named from the list in
   * configuration/contentIcons.json. Optional: a tile without one is still a
   * tile, and a set of tiles where only some carry a symbol reads worse than
   * none at all - which is a decision for whoever writes them.
   */
  icon?: string;
  /** One line that stays visible while the tile is closed. */
  summary?: string;
  /** The folded-away text. */
  text?: string;
  link?: BlockLink;
}

/**
 * A grid of tiles, each folding open on a short text.
 *
 * Close to the FAQ block and deliberately not the same thing: an FAQ is a
 * column of questions read in order, this is a set of topics laid out side by
 * side, each of which can also point somewhere else. It is what a resources
 * page is made of - "What is PEM?", "What is a care pathway?" - where the tile
 * answers in three sentences and the link leads to the material itself.
 */
export interface TopicsBlock extends BaseBlock {
  type: 'topics';
  title?: string;
  description?: string;
  items: TopicItem[];
  width: BlockWidth;
  /**
   * Compact tiles: four or five to a row, each showing its symbol and its
   * title and nothing else until it is opened.
   *
   * The wide form is a set of topics to read; the compact form is a shelf to
   * choose from, which is what a library page is. Two layouts rather than one
   * that guesses, because the difference is what the page is for and only the
   * person writing it knows that.
   */
  compact: boolean;
}

export type Block =
  | TextBlock
  | CardsBlock
  | ListBlock
  | TeasersBlock
  | CtaBlock
  | QuoteBlock
  | ImageBlock
  | DownloadsBlock
  | LinksBlock
  | FaqBlock
  | TopicsBlock;

export type BlockType = Block['type'];
